import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { NextRouter } from 'next/dist/shared/lib/router/router'
import { DeletedEntityLabel } from '@omnea-app/fe-requests/DeletedEntityLabel'
import { isDeletedRequestRowSupplier } from '@omnea-app/fe-requests/useRequestTableRows'
import { generateSupplierRoute } from '@omnea-app/fe-suppliers/details/generateSupplierRoute'
import { ProfileBox } from '@omnea-app/frontend/components/shared/ui/profile-box/ProfileBox'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { getSupplierSearchFilterOperators } from '../../ControlledTable/components/getSupplierSearchFilterOperators'
import { RowGroupingColumnTableCell } from '../../row-grouping/components/RowGroupingColumnTableCell'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'
import { HoverLinkWrapper } from './helpers/HoverLinkWrapper'
import { RenderEditSupplierCell } from './helpers/RenderEditSupplierCell'
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows'

type Dependencies = {
  push: NextRouter['push']
}

export type SupplierColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'supplier'> & {
  columnType: 'supplier'
}

export const getSupplierColumn = <T extends GridValidRowModel>({
  dependencies: { push },
  style,
  ...config
}: SupplierColumnConfig<T> & { dependencies: Dependencies }): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  field: 'supplier',
  valueGetter: (_, row) => row.supplier?.name,
  groupingValueGetter: (value: any) => value?.name,
  filterOperators: getSupplierSearchFilterOperators(),
  renderCell: (params: GridRenderCellParams<T>) => {
    if (!isDefined(params.value))
      return isDeletedRequestRowSupplier(params.row.supplier) ? <DeletedEntityLabel type="supplier" /> : null
    if (params.rowNode.type === 'group') {
      return <RowGroupingColumnTableCell columnHeaderName={config.headerName} value={params.value} id={params.id} />
    }
    return (
      <HoverLinkWrapper {...(config?.getDataTestId ? { ['data-testid']: config.getDataTestId(params) } : {})}>
        <ProfileBox
          userId={null}
          text={params.value}
          imgUrl={params.row.supplier?.logoUrl}
          disableTooltip
          avatarVariant="rounded"
          onClick={(e) => {
            if (!isDefined(params.row.supplier?.id)) return
            e.stopPropagation()
            void push(generateSupplierRoute({ supplierId: params.row.supplier.id, tab: 'overview' }))
          }}
        />
      </HoverLinkWrapper>
    )
  },
  renderEditCell: (params) => {
    return <RenderEditSupplierCell selectedSupplierId={params.row.supplier?.id} id={params.id} />
  },
})
